/**
 * StatusItemsDrawer
 *
 * Opens when the user clicks a stat card on the Content Planner calendar.
 * Shows all items belonging to that status group with quick actions:
 *   - Edit  → /editor/blog/:id
 *   - View  → wp_url (new tab)
 *   - Delete → soft-delete with confirmation
 */

import React, { useState } from 'react'
import { Drawer, Popconfirm } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  PenLine,
  ExternalLink,
  Trash2,
  FileText,
  Calendar,
  BarChart2,
  Globe,
  AlertCircle,
} from 'lucide-react'
import { useDeleteContent } from '@/hooks/useBlogCreationAPI'
import { contentPlannerKeys } from '@/hooks/useContentPlannerAPI'
import type { CalendarItem } from '@/types/contentPlanner'
import { cn } from '@/lib/utils'
import { universalBlogEditorPath } from '@/utils/editorRoutes'
import { StatusBadge } from '../shared/StatusBadge'
import { GradeBadge } from '../shared/GradeBadge'
import dayjs from 'dayjs'

// ── WP publish status badge ────────────────────────────────────────────────

function WPStatusBadge({ status }: { status: string | null }) {
  if (!status) return null

  const map: Record<string, { label: string; cls: string }> = {
    published:  { label: 'WP Live',        cls: 'bg-coral-light text-coral-dark border-coral/20' },
    scheduled:  { label: 'WP Scheduled',   cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
    pending:    { label: 'WP Pending',      cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
    publishing: { label: 'Publishing…',     cls: 'bg-teal-light text-teal-dark border-teal/20 animate-pulse' },
    enhancing:  { label: 'Enhancing…',      cls: 'bg-teal-light text-teal-dark border-teal/20 animate-pulse' },
    failed:     { label: 'WP Failed',       cls: 'bg-destructive/5 text-destructive border-destructive/20' },
  }

  const cfg = map[status]
  if (!cfg) return null

  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded border', cfg.cls)}>
      {cfg.label}
    </span>
  )
}

// ── Item row ───────────────────────────────────────────────────────────────

function ItemRow({ item, onDeleted }: { item: CalendarItem; onDeleted: (id: string) => void }) {
  const navigate = useNavigate()
  const { mutate: deleteContent, isPending: isDeleting } = useDeleteContent()
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)

  function handleDelete() {
    deleteContent(item.id, {
      onSuccess: () => {
        // Also invalidate calendar cache (useDeleteContent only invalidates content list)
        queryClient.invalidateQueries({ queryKey: contentPlannerKeys.all })
        onDeleted(item.id)
      },
    })
  }

  return (
    <div
      className="flex items-start gap-3 px-4 py-3.5 border-b last:border-0 hover:bg-warm-bg/60 transition-colors group cursor-pointer"
      onClick={() => navigate(universalBlogEditorPath(item.id))}
    >
      {/* Icon */}
      <div className="mt-0.5 w-8 h-8 rounded-lg bg-muted/40 flex items-center justify-center shrink-0">
        <FileText className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug line-clamp-2 mb-1.5">
          {item.title || item.keyword || 'Untitled'}
        </p>

        {/* Badges row */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <StatusBadge status={item.status} size="sm" />
          <WPStatusBadge status={item.publish_status} />
          <GradeBadge grade={item.grade} />
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          {item.word_count != null && (
            <span className="flex items-center gap-1">
              <BarChart2 className="h-2.5 w-2.5" />
              {item.word_count.toLocaleString()} words
            </span>
          )}
          {item.scheduled_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-2.5 w-2.5" />
              {dayjs(item.scheduled_date).format('MMM D, YYYY')}
            </span>
          )}
          {item.wp_url && (
            <span className="flex items-center gap-1 text-teal-dark">
              <Globe className="h-2.5 w-2.5" />
              Live
            </span>
          )}
        </div>
      </div>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          title="Edit article"
          className="p-1.5 rounded-md hover:bg-teal-light text-muted-foreground hover:text-teal-dark transition-colors"
          onClick={(event) => {
            event.stopPropagation()
            navigate(universalBlogEditorPath(item.id))
          }}
        >
          <PenLine className="h-3.5 w-3.5" />
        </button>

        {item.wp_url && (
          <a
            href={item.wp_url}
            target="_blank"
            rel="noopener noreferrer"
            title="View live on site"
            className="p-1.5 rounded-md hover:bg-coral-light text-muted-foreground hover:text-coral-dark transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}

        <Popconfirm
          open={confirmOpen}
          title="Delete article?"
          description="This will soft-delete the article. Published WP posts will also be removed."
          okText="Delete"
          okButtonProps={{ danger: true, loading: isDeleting }}
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setConfirmOpen(false)}
          placement="leftTop"
        >
          <button
            title="Delete article"
            className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            onClick={(event) => {
              event.stopPropagation()
              setConfirmOpen(true)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </Popconfirm>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export interface StatusItemsDrawerProps {
  open: boolean
  statusLabel: string
  statusIcon: React.ReactNode
  items: CalendarItem[]
  onClose: () => void
}

export function StatusItemsDrawer({
  open,
  statusLabel,
  statusIcon,
  items,
  onClose,
}: StatusItemsDrawerProps) {
  // Track locally deleted IDs so rows disappear immediately before cache refresh
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())

  const visibleItems = items.filter((i) => !deletedIds.has(i.id))

  function handleDeleted(id: string) {
    setDeletedIds((prev) => new Set([...prev, id]))
  }

  // Reset deleted set when drawer closes
  React.useEffect(() => {
    if (!open) setDeletedIds(new Set())
  }, [open])

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      title={
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{statusIcon}</span>
          <span className="font-semibold">{statusLabel}</span>
          <span className="ml-auto text-sm font-normal text-muted-foreground">
            {visibleItems.length} article{visibleItems.length !== 1 ? 's' : ''}
          </span>
        </div>
      }
      styles={{ body: { padding: 0 } }}
    >
      {visibleItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center px-8">
          <AlertCircle className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No articles here</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Articles in this status will appear here.
          </p>
        </div>
      ) : (
        <div>
          {visibleItems.map((item) => (
            <ItemRow key={item.id} item={item} onDeleted={handleDeleted} />
          ))}
        </div>
      )}
    </Drawer>
  )
}
